import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Organizacja } from '../../models/organizacja';
import { AdminPanelUserDetails } from '../../models/admin-panel-user-details';
import { AdminPanelUserOrganizationDetails } from '../../models/admin-panel-user-organization-details';
import { AdminPanelUserOrganizationRelatedPerson } from '../../models/admin-panel-user-organization-related-person';
import { TableResponse } from '../../models/table-response';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { TableRequestData } from '../../models/table/table-request-param';
import { HttpUtils } from '../../utils/http.utils';
import { ReprezentantOrganizacji } from '../../models/reprezentant-organizacji';
import { OrganizationRepresentativeUpdateRoleReq } from '../../models/custom-types';
import { Zalacznik } from '../../models/zalacznik';

@Injectable({
  providedIn: 'root',
})
export class OrganizacjaApi {
  #httpClient: HttpClient = inject(HttpClient);
  readonly #apiUrl = environment.API_EWNIOSKI + '/organizacja';
  readonly #adminApiUrl = environment.API_EWNIOSKI + '/admin/uzytkownicy';

  public getOrganizationRepresentatives$(
    tableRequest: TableRequestData,
  ): Observable<TableResponse<ReprezentantOrganizacji>> {
    return this.#httpClient.get<TableResponse<ReprezentantOrganizacji>>(
      `${this.#apiUrl}/reprezentanci`,
      {
        params: HttpUtils.toHttpParams(tableRequest),
        withCredentials: true,
      },
    );
  }

  public deleteOrganizationRepresentative$(
    idBeneficjenta: number,
  ): Observable<void> {
    const httpParams = new HttpParams().append('id', idBeneficjenta);
    return this.#httpClient.delete<void>(`${this.#apiUrl}/reprezentant`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  public updateOrganizationRepresentativeRole$(
    req: OrganizationRepresentativeUpdateRoleReq,
  ): Observable<void> {
    return this.#httpClient.put<void>(
      `${this.#apiUrl}/reprezentant/updateRola`,
      {
        idBeneficjenta: req.idBeneficjetna,
        rola: req.rola,
        instytucje: req.instytucje,
      } as Partial<ReprezentantOrganizacji>,
      {
        withCredentials: true,
      },
    );
  }

  public saveRepresentativePowerOfAttorney$(
    idBeneficjenta: number,
    file: File,
  ): Observable<Zalacznik> {
    const formData = new FormData();
    const json = JSON.stringify(idBeneficjenta);
    const blob = new Blob([json], {
      type: 'application/json',
    });
    formData.append('beneficjentId', blob);
    formData.append('file', file);

    return this.#httpClient.post<Zalacznik>(
      `${this.#apiUrl}/reprezentant/savePelnomocnictwoReprezentanta`,
      formData,
      {
        withCredentials: true,
      },
    );
  }

  public deleteRepresentativePowerOfAttorney$(
    pelnomocnictwoId: number,
  ): Observable<void> {
    const httpParams = new HttpParams().append(
      'pelnomocnictwoId',
      pelnomocnictwoId.toString(),
    );
    return this.#httpClient.delete<void>(
      `${this.#apiUrl}/reprezentant/deletePelnomocnictwoReprezentanta`,
      {
        params: httpParams,
        withCredentials: true,
      },
    );
  }

  downloadRepresentativePowers$(ids: number[]): Observable<HttpResponse<Blob>> {
    return this.#httpClient.get(
      `${this.#apiUrl}/reprezentant/downloadPelnomocnictwa`,
      {
        params: { ids },
        observe: 'response',
        responseType: 'blob',
        withCredentials: true,
      },
    );
  }

  public emailByPesel$(pesel: string): Observable<string> {
    const params = new HttpParams().append('pesel', pesel);

    return this.#httpClient.get(`${this.#apiUrl}/email-by-pesel`, {
      params,
      withCredentials: true,
      responseType: 'text' as const,
    });
  }

  public joinOrganizationByToken$(token: string): Observable<void> {
    const params = new HttpParams().append('token', token);

    return this.#httpClient.post<void>(
      `${this.#apiUrl}/representative/join`,
      {},
      {
        params,
        withCredentials: true,
      },
    );
  }

  public joinOrganizationByIdentity$(): Observable<number> {
    return this.#httpClient.post<number>(
      `${this.#apiUrl}/representative/join-by-identity`,
      {},
      {
        withCredentials: true,
      },
    );
  }

  public inviteRepresentative$(daneZaproszenia: {
    pesel: string;
    email: string;
    rola: string;
    instytucje: string[];
    pelnomocnictwa: File[];
  }) {
    const formData = new FormData();

    const json = JSON.stringify({
      pesel: daneZaproszenia.pesel,
      email: daneZaproszenia.email,
      rola: daneZaproszenia.rola,
      instytucje: daneZaproszenia.instytucje,
    });
    formData.append('data', new Blob([json], { type: 'application/json' }));

    daneZaproszenia.pelnomocnictwa.forEach((file) =>
      formData.append('files', file),
    );

    return this.#httpClient.post<void>(
      `${this.#apiUrl}/representative/invite`,
      formData,
      {
        withCredentials: true,
      },
    );
  }

  public getInstytucje$(): Observable<string[]> {
    return this.#httpClient.get<string[]>(`${this.#apiUrl}/institutions`, {
      withCredentials: true,
    });
  }

  public getAdminOrganizationDetails$(
    userDetails: AdminPanelUserDetails,
    organizationId: number,
  ): Observable<AdminPanelUserOrganizationDetails> {
    return this.#httpClient.get<AdminPanelUserOrganizationDetails>(
      `${this.#adminApiUrl}/organizacje/${organizationId}`,
      {
        params: HttpUtils.toHttpParams(
          HttpUtils.withAdminPanelUserIdentityParams(userDetails),
        ),
        withCredentials: true,
      },
    );
  }

  public getUsersRelatedToOrganizationForAdminPanel$(
    userDetails: AdminPanelUserDetails,
    organizationId: number,
    tableRequest: TableRequestData,
  ): Observable<TableResponse<AdminPanelUserOrganizationRelatedPerson>> {
    return this.#httpClient.get<
      TableResponse<AdminPanelUserOrganizationRelatedPerson>
    >(
      `${this.#adminApiUrl}/organizacje/${organizationId}/related-persons`,
      {
        params: HttpUtils.toHttpParams(
          HttpUtils.withAdminPanelUserIdentityParams(
            userDetails,
            tableRequest,
          ),
        ),
        withCredentials: true,
      },
    );
  }
}
